import type { AvailableField } from "@/components/Table/Filters/ExportModal";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import type { CaseFilter } from "@/queries/useFetchCaseFiltersets.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseMutationResult, useMutation } from "@tanstack/react-query";
import { handleJsonApiError } from "jsonapi-zod-query";
import type { MRT_ColumnFiltersState, MRT_SortingState } from "material-react-table";

export const useExportCases = (): UseMutationResult<
    void,
    Error,
    {
        fieldsInOrder: AvailableField[];
        isQueryEnabled?: boolean;
        selectedTab?: string;
        columnFilters: MRT_ColumnFiltersState;
        globalFilter: string;
        activeFilter: CaseFilter | null;
        sorting: MRT_SortingState;
    }
> => {
    const fetch = useAuthenticatedFetch();

    return useMutation({
        mutationFn: async ({
            fieldsInOrder,
            isQueryEnabled,
            selectedTab,
            columnFilters,
            globalFilter,
            activeFilter,
            sorting,
        }) => {
            const response = await fetch(apiUrl("/dashboard/export-cases"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/vnd.api+json",
                },
                body: JSON.stringify({
                    fields: fieldsInOrder,
                    isQueryEnabled,
                    selectedTab,
                    filters: columnFilters,
                    globalFilter,
                    activeFilter,
                    sorting,
                }),
            });

            // Handle the ZIP file response
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;

                const contentDisposition = response.headers.get("content-disposition");
                let filename = `AdvancedFiltersFront-${new Date().toISOString().slice(0, 10)}.zip`;

                if (contentDisposition) {
                    const filenameMatch = contentDisposition.match(/filename=([^;]+)/i);

                    if (filenameMatch) {
                        filename = filenameMatch[1].trim();
                    }
                }

                a.download = filename;

                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                a.remove();
            } else {
                await handleJsonApiError(response);
            }
        },
    });
};
